import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchLoading } from '@shared/hooks/useSearchLoading';
import type { FilterOption } from '@shared/components/FilterPopup';
import type { DateRange } from '@shared/components/FilterPopup/types';
import { listAdminServiceRequestsApiService } from '../../api/listAdminServiceRequestsApiService';
import type {
  AdminServiceRequestListItem,
  ServiceRequestStatus,
} from '../../types/serviceRequestListType';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Oczekuje', value: 'pending', checked: false },
  { label: 'W trakcie weryfikacji', value: 'in_review', checked: false },
  { label: 'Przydzielone', value: 'assigned', checked: false },
  { label: 'Zaakceptowane', value: 'accepted', checked: false },
  { label: 'W realizacji', value: 'in_progress', checked: false },
  { label: 'Opóźnione', value: 'delayed', checked: false },
  { label: 'Zakończone', value: 'completed', checked: false },
  { label: 'Zamknięte', value: 'closed', checked: false },
  { label: 'Odrzucone', value: 'rejected', checked: false },
];

const VALID_STATUSES = DEFAULT_STATUS_OPTIONS.map(
  (o) => o.value
) as ServiceRequestStatus[];

const isValidStatus = (s: string): boolean =>
  (VALID_STATUSES as string[]).includes(s);

const isValidSortOrder = (s: string): s is 'asc' | 'desc' =>
  s === 'asc' || s === 'desc';

const sanitizeStatusList = (values: string[]): string =>
  values.filter(isValidStatus).join(',');

const statusListFromParam = (raw: string | null): string[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(isValidStatus);
};

const buildStatusOptions = (selected: string[]): FilterOption[] =>
  DEFAULT_STATUS_OPTIONS.map((o) => ({
    ...o,
    checked: selected.includes(o.value),
  }));

const sameSelectedStatuses = (a: FilterOption[], b: FilterOption[]): boolean =>
  a.every((opt, i) => opt.checked === b[i]?.checked);

export interface UseAdminServiceRequestsListResult {
  items: AdminServiceRequestListItem[];
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  totalCount: number;
  search: string;
  setSearch: (search: string) => void;
  sortColumn: string | null;
  sortOrder: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;
  statusOptions: FilterOption[];
  setStatusOptions: (options: FilterOption[]) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  appliedFilters: { statusOptions: FilterOption[]; dateRange: DateRange };
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
  limitError: string;
  handleLimitChange: (limit: number) => void;
  hasNoData: boolean;
  isSearchClearing: boolean;
}

export function useAdminServiceRequestsList(): UseAdminServiceRequestsListResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  // ---- Initial state from URL ----
  const initial = (() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlStatusList = statusListFromParam(searchParams.get('status'));
    const urlStartDate = searchParams.get('startDate') ?? undefined;
    const urlEndDate = searchParams.get('endDate') ?? undefined;

    const pageNum = urlPage ? parseInt(urlPage, 10) : 1;
    const limitNum = urlLimit ? parseInt(urlLimit, 10) : 10;

    return {
      page: !isNaN(pageNum) && pageNum >= 1 ? pageNum : 1,
      limit: (ALLOWED_LIMITS as readonly number[]).includes(limitNum)
        ? limitNum
        : 10,
      search: urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch,
      sortOrder:
        urlSortOrder && isValidSortOrder(urlSortOrder) ? urlSortOrder : 'desc',
      statusOptions: buildStatusOptions(urlStatusList),
      startDate: urlStartDate,
      endDate: urlEndDate,
    };
  })();

  const {
    search,
    debouncedSearch,
    setSearch: setSearchWithLoad,
    setSearchDirect: setSearchState,
    loading,
    setLoading,
    effectiveLoading,
  } = useSearchLoading(initial.search, 500);

  const [items, setItems] = useState<AdminServiceRequestListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  const [sortColumn] = useState<string | null>('submitted_on');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initial.sortOrder);
  const [limitError, setLimitError] = useState('');

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(
    initial.statusOptions
  );
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: initial.startDate,
    endDate: initial.endDate,
  });
  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
    dateRange: DateRange;
  }>({
    statusOptions: initial.statusOptions,
    dateRange: { startDate: initial.startDate, endDate: initial.endDate },
  });

  const hasFetchedRef = useRef(false);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  // -------------------------------------------------------------------------
  // URL → state (browser back/forward)
  // -------------------------------------------------------------------------
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlStatusList = statusListFromParam(searchParams.get('status'));

    const nextPage = (() => {
      const v = urlPage ? parseInt(urlPage, 10) : 1;
      return !isNaN(v) && v >= 1 ? v : 1;
    })();
    if (nextPage !== page) {
      setPage(nextPage);
    }

    const nextLimit = (() => {
      const v = urlLimit ? parseInt(urlLimit, 10) : 10;
      return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
    })();
    if (nextLimit !== limit) {
      setLimit(nextLimit);
    }

    const nextSearch =
      urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch;
    if (nextSearch !== search) {
      setSearchState(nextSearch);
      if (nextSearch.trim() !== search.trim()) {
        setLoading(true);
      }
    }

    const nextSortOrder: 'asc' | 'desc' =
      urlSortOrder && isValidSortOrder(urlSortOrder) ? urlSortOrder : 'desc';
    if (nextSortOrder !== sortOrder) {
      setSortOrder(nextSortOrder);
    }

    const nextStatusOptions = buildStatusOptions(urlStatusList);
    if (!sameSelectedStatuses(nextStatusOptions, statusOptions)) {
      setStatusOptions(nextStatusOptions);
    }
    if (
      !sameSelectedStatuses(nextStatusOptions, appliedFilters.statusOptions)
    ) {
      setAppliedFilters((prev) => ({
        ...prev,
        statusOptions: nextStatusOptions,
      }));
    }

    const nextStartDate = searchParams.get('startDate') ?? undefined;
    const nextEndDate = searchParams.get('endDate') ?? undefined;
    setDateRange((prev) => {
      if (prev.startDate === nextStartDate && prev.endDate === nextEndDate) {
        return prev;
      }
      return { startDate: nextStartDate, endDate: nextEndDate };
    });
    setAppliedFilters((prev) => {
      if (
        prev.dateRange.startDate === nextStartDate &&
        prev.dateRange.endDate === nextEndDate
      ) {
        return prev;
      }
      return {
        ...prev,
        dateRange: { startDate: nextStartDate, endDate: nextEndDate },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset isSyncingFromUrl after state settles
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, sortOrder, appliedFilters]);

  // -------------------------------------------------------------------------
  // state → URL  (always preserves tab=serwisowe)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }

    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    const selectedStatuses = sanitizeStatusList(
      appliedFilters.statusOptions.filter((o) => o.checked).map((o) => o.value)
    );
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }

    if (sortOrder !== 'desc') {
      params.set('sortOrder', sortOrder);
    }

    if (appliedFilters.dateRange.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }

    // Preserve params owned by other components (tab, cols).
    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [
    page,
    limit,
    search,
    sortOrder,
    appliedFilters,
    searchParams,
    setSearchParams,
  ]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleSort = useCallback((columnKey: string) => {
    if (columnKey !== 'submitted_on') {
      return;
    }
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Allowed values: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ statusOptions, dateRange });
    setPage(1);
  }, [statusOptions, dateRange]);

  const handleClearFilters = useCallback(() => {
    const hasAppliedFilters =
      appliedFilters.statusOptions.some((o) => o.checked) ||
      !!appliedFilters.dateRange.startDate ||
      !!appliedFilters.dateRange.endDate;

    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    setDateRange({});

    if (hasAppliedFilters) {
      setAppliedFilters({
        statusOptions: [...DEFAULT_STATUS_OPTIONS],
        dateRange: {},
      });
      setPage(1);
    }
  }, [appliedFilters]);

  const setSearch = useCallback(
    (next: string) => {
      if (next.length > 200) {
        return;
      }
      setSearchWithLoad(next);
      setPage(1);
    },
    [setSearchWithLoad]
  );

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedSearch = debouncedSearch.trim();
      const selectedStatuses = sanitizeStatusList(
        appliedFilters.statusOptions
          .filter((o) => o.checked)
          .map((o) => o.value)
      );

      const response =
        await listAdminServiceRequestsApiService.listServiceRequests({
          page,
          limit,
          ...(trimmedSearch && { search: trimmedSearch }),
          ...(selectedStatuses && { status: selectedStatuses }),
          ...(sortOrder !== 'desc' && { sortOrder }),
          ...(appliedFilters.dateRange.startDate && {
            startDate: appliedFilters.dateRange.startDate,
          }),
          ...(appliedFilters.dateRange.endDate && {
            endDate: appliedFilters.dateRange.endDate,
          }),
        });

      const { totalCount: count, totalPages } = response.pagination;

      if (count > 0 && totalPages > 0 && page > totalPages) {
        setPage(totalPages);
        return;
      }

      setItems(response.data);
      setTotalCount(count);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      hasFetchedRef.current = true;
    }
  }, [page, limit, debouncedSearch, sortOrder, appliedFilters, setLoading]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const hasActiveFilters =
    appliedFilters.statusOptions.some((o) => o.checked) ||
    !!appliedFilters.dateRange.startDate ||
    !!appliedFilters.dateRange.endDate;

  const hasNoData =
    !loading &&
    hasFetchedRef.current &&
    page === 1 &&
    totalCount === 0 &&
    !debouncedSearch.trim() &&
    !hasActiveFilters;

  const isSearchClearing =
    !search.trim() && debouncedSearch.trim() !== '' && !hasActiveFilters;

  const justClearedSearch =
    prevDebouncedSearchRef.current.trim() !== '' &&
    debouncedSearch.trim() === '' &&
    !hasActiveFilters;

  return {
    items,
    loading: effectiveLoading,
    page,
    setPage,
    limit,
    totalCount,
    search,
    setSearch,
    sortColumn,
    sortOrder,
    handleSort,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    hasNoData,
    isSearchClearing: isSearchClearing || justClearedSearch,
  };
}
